import React from "react";
import { ActivityIndicator, FlatList, ScrollView } from "react-native";
import { RouteProp, useIsFocused } from "@react-navigation/native";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { kRoute } from "src/kRoute";
import { setBackButtonRoute } from "src/redux/navigation/thunkActions";
import { getItemPrice } from "src/util/functions";
import { kColors } from "../kColors";
import {
  displayProperConnectionSource,
  formatDate,
  formatMoney,
  formatPaymentMethod,
  formatPhoneNumber,
  formatShortAddress,
} from "../util/gFormatting";
import { kStyleConst } from "src/kStyle";
import { PdArrowUpRight, Printer } from "src/components/Icons";
import { TmtTitleHeader } from "src/components/impComponents";
import { PdBadge, PdBadgeLabel } from "src/components/layout";
import { getThemeColorFromOrderStatus } from "src/util/ui";
import { getDefaultAddress } from "src/util/user";
import { useAppDispatch } from "src/redux";
import { tCartProduct, tOrder } from "tmt_api/src/types/types";
import * as orderDb from "src/db/order.db";
import {
  PdDivider,
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableEmpty,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/layout/table";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { useIsMobile } from "src/hooks/ui";
import { TesterBadge } from "src/components/ui";
import { getProductInfoFromCartProduct } from "src/util/cart";

type tpViewOrderParamList = {
  order: {
    id: string;
  };
};

type tTableProduct = {
  id: string;
  productId: string;
  brandId: string;
  brandName: string;
  name: string;
  sku: string;
  quantity: number;
  originalQuantity?: number;
  caseMinimum: number;
  costPerUnit: number;
  total: number;
  isTester: boolean;
};

const mapTableProduct = (product: tCartProduct): tTableProduct => {
  const { caseMinimum, isTester } = getProductInfoFromCartProduct(product);
  return {
    id: product.isTester ? product.tester?.sku ?? `${product.id ?? ""}` : product.actualSKU,
    productId: product.productId,
    brandId: product.brandUid,
    brandName: product.brandName,
    name: product.name,
    sku: product.actualSKU,
    quantity: product.quantity,
    originalQuantity: product.originalQuantity,
    caseMinimum,
    costPerUnit: !product.quantity ? 0 : getItemPrice(product) / product.quantity,
    total: product.totalPrice,
    isTester,
  };
};

const ProductsTable: React.FC<{ order: tOrder }> = ({ order }) => {
  const tableInstance = useTable<tTableProduct>({ data: order.cart.products.map(mapTableProduct), columns });
  const isMobile = useIsMobile();

  const renderItem = ({ item }: { item: tRowInstance<tTableProduct> }) => {
    return (
      <PdTableRow>
        {item.cells.map((cell) => (
          <PdTableCell key={cell.uniqueId} style={[styles.heightAuto, cell.style]}>
            {cell.render("Cell")}
          </PdTableCell>
        ))}
      </PdTableRow>
    );
  };

  return (
    <PdTable
      ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
      ListFooterComponent={<PdDivider style={styles.tableDivider} />}
      data={tableInstance.rows}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      contentContainerStyle={styles.mrReset}
    />
  );

  return (
    <ScrollView horizontal contentContainerStyle={isMobile ? {} : styles.flex1}>
      <FlatList
        ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
        ListEmptyComponent={PdTableEmpty}
        ListFooterComponent={<PdDivider style={styles.tableDivider} />}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
      />
    </ScrollView>
  );
};

const Content: React.FC<{ order: tOrder }> = ({ order }) => {
  const pageRef = React.useRef(null);
  const dispatch = useAppDispatch();
  const rIsMobile = useSelector(
    (state) => state.NavigationReducer.deviceSize === "xs" || state.NavigationReducer.deviceSize === "sm",
  );
  const { orderStatus, cart, connection, paymentMethod, dateCreated, orderMessage, orderNumber, dateShipped } = order;
  const discountAmount = cart.discountCost ?? 0;
  const marketplaceFeeAmount = cart.commissionFee ?? 0;
  const serviceFeeAmount = cart.serviceFee ?? 0;
  const rRetailDefaultAddress = getDefaultAddress(connection.boutique.addresses);
  const rPONumber = cart.PONumber;
  const rOrderSource = cart.connectionSource;
  const hasRep = cart.rep && cart.rep?.id && cart.rep?.vendorId;
  const rShippingAddress = cart.address;
  const repCommissionAmount = cart.totalPrice * (cart.rep?.repPercent ?? 0);

  const total = formatMoney(
    cart.totalPrice +
      cart.totalPriceWithShipping -
      (hasRep ? repCommissionAmount : 0) -
      marketplaceFeeAmount -
      serviceFeeAmount,
  );

  React.useEffect(() => {
    dispatch(setBackButtonRoute(kRoute.P16));
  }, [dispatch]);

  return (
    <View ref={pageRef} style={styles.flex1}>
      <ScrollView
        contentContainerStyle={[styles.bgWhite, rIsMobile ? [styles.mx5, styles.my3] : [styles.mx10, styles.my5]]}
      >
        <View nativeID="print-header">
          <TmtTitleHeader style={[styles.printHeaderContainer, styles.mb5]} svgStyle={styles.printHeader} />
        </View>
        <TouchableOpacity style={[styles.printerButton, styles.mb3]}>
          <Printer color={kColors.brand500} style={[styles.printerLeftIcon, styles.mr1]} />
          <Text style={styles.printerLabel}>Print</Text>
          <PdArrowUpRight style={[styles.printerRightIcon, styles.ml1]} />
        </TouchableOpacity>
        <View style={[styles.P16_01_OrderTitleView, styles.flexWrap]}>
          <View>
            <View style={styles.P16_01_OrderTitleLeftSubView}>
              <Text style={styles.orderName}>{connection.boutique.name}</Text>
              {orderStatus && (
                <PdBadge style={[styles.ml2, { backgroundColor: getThemeColorFromOrderStatus(orderStatus) }]}>
                  <PdBadgeLabel>{orderStatus}</PdBadgeLabel>
                </PdBadge>
              )}
            </View>
            {!!rRetailDefaultAddress.name && <Text style={styles.headerText}>{rRetailDefaultAddress.name}</Text>}
            <Text style={styles.headerText}>{rRetailDefaultAddress.address1}</Text>
            {!!rRetailDefaultAddress.address2 && (
              <Text style={styles.headerText}>{rRetailDefaultAddress.address2}</Text>
            )}
            <Text style={styles.headerText}>{formatShortAddress(rRetailDefaultAddress)}</Text>
            {!!rRetailDefaultAddress.phone && (
              <Text style={styles.headerText}>{formatPhoneNumber(rRetailDefaultAddress.phone)}</Text>
            )}
            <Text style={styles.headerText}>Order #: {orderNumber}</Text>
            {!!rPONumber && <Text style={styles.headerText}>PO #: {rPONumber}</Text>}
            <Text style={styles.headerSecondaryText}>{connection.brand.name}</Text>
          </View>
          <View style={rIsMobile ? null : styles.P16_01_OrderTitleRightView}>
            <View style={[styles.dl, styles.mb3]}>
              <Text style={styles.dt}>Order Date</Text>
              <Text style={styles.dd}>{dateCreated?.seconds ? formatDate(dateCreated.seconds * 1000) : ""}</Text>
            </View>
            <View style={[styles.dl, styles.mb3]}>
              <Text style={styles.dt}>Order Source</Text>
              <Text style={styles.dd}>{hasRep ? "Sales Rep" : displayProperConnectionSource(rOrderSource)}</Text>
            </View>
            <View style={styles.dl}>
              <Text style={styles.dt}>Payment Terms</Text>
              <Text style={styles.dd}>{formatPaymentMethod(paymentMethod)}</Text>
            </View>
          </View>
        </View>
        <ProductsTable order={order} />
        <View style={rIsMobile ? styles.my2 : [styles.flexRow, styles.flexWrap, styles.my5]}>
          <View style={rIsMobile ? styles.mb3 : styles.mr4}>
            <Text style={styles.dt}>Shipped to</Text>
            <View style={[styles.divider, styles.mb3]} />
            <View style={styles.mr10}>
              {!!rShippingAddress.name && <Text style={styles.infoText}>{rShippingAddress.name}</Text>}
              <Text style={styles.infoText}>{rShippingAddress.address1}</Text>
              {!!rShippingAddress.address2 && <Text style={styles.infoText}>{rShippingAddress.address2}</Text>}
              <Text style={styles.infoText}>{formatShortAddress(rShippingAddress)}</Text>
              {!!rShippingAddress.phone && (
                <Text style={styles.infoText}>{formatPhoneNumber(rShippingAddress.phone)}</Text>
              )}
            </View>
          </View>
          <View style={rIsMobile ? styles.mb3 : styles.mr4}>
            <Text style={styles.dt}>Details</Text>
            <View style={[styles.divider, styles.mb3]} />
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.wSm]}>Tracking Number</Text>
              <Text style={styles.infoText}>{cart.shippingNumber || ""}</Text>
            </View>
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.wSm]}>Shipped Date</Text>
              <Text style={styles.infoText}>{dateShipped ? formatDate(dateShipped.seconds * 1000) : ""}</Text>
            </View>
          </View>
          <View style={[styles.mlAuto, styles.zIndexLess1]}>
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.tar]}>Subtotal</Text>
              <Text style={[styles.infoText, styles.flex1, styles.tar]}>
                {formatMoney(cart.totalPrice + discountAmount)}
              </Text>
            </View>
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.tar]}>Shipping and Handling</Text>
              <Text style={[styles.infoText, styles.flex1, styles.tar]}>
                {cart.shippingNumber || formatMoney(cart.totalPriceWithShipping)}
              </Text>
            </View>
            {!!discountAmount && (
              <View style={styles.detailsRow}>
                <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.tar]}>Discount</Text>
                <Text style={[styles.infoText, styles.flex1, styles.tar]}>-{formatMoney(discountAmount)}</Text>
              </View>
            )}
            {rOrderSource === "TMT" && !hasRep && (
              <View style={styles.detailsRow}>
                <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.tar]}>
                  Calculated Commission
                </Text>
                <Text style={[styles.infoText, styles.flex1, styles.tar]}>{formatMoney(marketplaceFeeAmount)}</Text>
              </View>
            )}
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.tar]}>Service Fee</Text>
              <Text style={[styles.infoText, styles.flex1, styles.tar]}>{formatMoney(serviceFeeAmount)}</Text>
            </View>
            {hasRep && (
              <View style={styles.detailsRow}>
                <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.tar]}>Sales Rep Commission</Text>
                <Text style={[styles.infoText, styles.flex1, styles.tar]}>{formatMoney(repCommissionAmount)}</Text>
              </View>
            )}
            <View style={[styles.divider, styles.my2]} />
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.tar]}>Total</Text>
              <Text style={[styles.infoText, styles.flex1, styles.tar]}>{total}</Text>
            </View>
          </View>
        </View>
        {!!orderMessage && (
          <View style={[styles.gLinkBankFormItemNoMargin, styles.zIndexLess1]}>
            <View style={styles.P16_01_OrderTitleLeftSubView}>
              <Text style={styles.dt}>Comment</Text>
            </View>
            <Text style={styles.infoText}>{orderMessage}</Text>
          </View>
        )}
      </ScrollView>
      <View nativeID="print-footer">
        <Text style={styles.printFooterLabel}>www.themaintab.com | info@themaintab.com</Text>
      </View>
    </View>
  );
};

const P16_01_ViewTransaction: React.FC<{ route: RouteProp<tpViewOrderParamList, "order"> }> = ({ route }) => {
  const isFocused = useIsFocused();
  const [order, setOrder] = React.useState<tOrder | undefined>(undefined);

  React.useEffect(() => {
    (async () => {
      const orderDocument = await orderDb.documentOrder(route.params?.id).get();
      setOrder(orderDocument.data());
    })();
  }, [route.params?.id, setOrder]);

  React.useEffect(() => {
    if (!isFocused) {
      setOrder(undefined);
    }
  }, [isFocused]);

  if (!order) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return <Content order={order} />;
};

export const P16_01 = React.memo(P16_01_ViewTransaction);

const styles = StyleSheet.create({
  P16_01_OrderTitleLeftSubView: {
    alignItems: "center",
    flexDirection: "row",
  },
  P16_01_OrderTitleRightView: {
    alignItems: "flex-end",
  },
  P16_01_OrderTitleView: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: kStyleConst.kSpacing * 4,
  },
  alignCenter: {
    alignItems: "center",
  },
  bgWhite: {
    backgroundColor: kColors.white,
  },
  dd: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontWeight: "500",
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  detailsRow: {
    flexDirection: "row",
  },
  divider: {
    backgroundColor: kColors.gray300,
    height: 0.75,
    width: "100%",
  },
  dl: {
    alignItems: "flex-end",
  },
  dt: {
    color: kColors.brandDark300,
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 1,
    lineHeight: 18,
    textTransform: "uppercase",
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  flexWrap: {
    flexWrap: "wrap",
  },
  gLinkBankFormItemNoMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  headerSecondaryText: {
    fontFamily: "Raleway",
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 1,
    lineHeight: 30,
    textTransform: "uppercase",
  },
  headerText: {
    color: kColors.gray500,
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: "400",
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  heightAuto: {
    height: "100%",
  },
  infoSecondaryText: {
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 1,
    lineHeight: 30,
    textTransform: "uppercase",
  },
  infoText: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: "400",
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  justifyCenter: {
    justifyContent: "center",
  },
  maxW300: {
    maxWidth: 300,
  },
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: 5 * kStyleConst.kSpacing,
  },
  ml1: {
    marginLeft: kStyleConst.kSpacing,
  },
  ml2: {
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mr1: {
    marginRight: kStyleConst.kSpacing,
  },
  mr10: {
    marginRight: 10 * kStyleConst.kSpacing,
  },
  mr4: {
    marginRight: 4 * kStyleConst.kSpacing,
  },
  mrReset: {
    marginRight: "initial",
  },
  mx10: {
    marginHorizontal: 10 * kStyleConst.kSpacing,
  },
  mx5: {
    marginHorizontal: 5 * kStyleConst.kSpacing,
  },
  my2: {
    marginVertical: 2 * kStyleConst.kSpacing,
  },
  my3: {
    marginVertical: 3 * kStyleConst.kSpacing,
  },
  my5: {
    marginVertical: 5 * kStyleConst.kSpacing,
  },
  orderName: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 22,
    fontWeight: "500",
    letterSpacing: 1.1,
  },
  printFooterLabel: {
    color: kColors.gray500,
    fontFamily: "Poppins",
    fontWeight: "500",
  },
  printHeader: {
    maxWidth: 220,
  },
  printHeaderContainer: {
    justifyContent: "center",
    minHeight: 25,
  },
  printerButton: {
    alignItems: "center",
    flexDirection: "row",
  },
  printerLabel: {
    color: kColors.brand500,
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  printerLeftIcon: {
    height: 20,
    width: 21,
  },
  printerRightIcon: {
    height: 7,
    width: 7,
  },
  tableDivider: {
    marginVertical: kStyleConst.kSpacing * 2,
  },
  tar: {
    textAlign: "right",
  },
  wMd: {
    width: 200,
  },
  wSm: {
    width: 130,
  },
  zIndexLess1: {
    zIndex: -1,
  },
});

const columns: tColumn<tTableProduct>[] = [
  {
    id: "name",
    header: "Product",
    accessor: (originalRow) => originalRow.name,
    Cell: ({ value, row, style }) => (
      <View style={[styles.flexRow, styles.flexWrap, styles.alignCenter, style]}>
        <PdTableCellText numberOfLines={undefined} style={styles.maxW300}>
          {value}
        </PdTableCellText>
        {row.isTester && <TesterBadge style={styles.ml2} />}
      </View>
    ),
    containerStyle: { flex: 1 },
    style: { flex: 1 },
  },
  {
    id: "sku",
    header: "SKU",
    accessor: (originalRow) => originalRow.sku,
    Cell: ({ value }) => <PdTableCellText numberOfLines={undefined}>{value}</PdTableCellText>,
    containerStyle: { flex: 1 },
    style: { flex: 1 },
  },
  {
    id: "quantity",
    header: "Quantity",
    accessor: (originalRow) => originalRow.quantity,
    Cell: ({ row }) => <PdTableCellText>{row.quantity}</PdTableCellText>,
    containerStyle: { flex: 1 },
    style: { flex: 1 },
  },
  {
    id: "costPerUnit",
    header: "Per Unit",
    accessor: (originalRow) => originalRow.costPerUnit,
    Cell: ({ row }) => <PdTableCellText>{formatMoney(row.costPerUnit)}</PdTableCellText>,
    containerStyle: { flex: 1 },
    style: { flex: 1 },
  },
  {
    id: "total",
    header: "Total",
    accessor: (originalRow) => originalRow.total,
    Cell: ({ row }) => <PdTableCellText>{formatMoney(row.total)}</PdTableCellText>,
    containerStyle: { flex: 1 },
    style: { flex: 1 },
  },
];
